import Card from '@/components/common/Card'
import FormField from '@/components/common/FormField'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import type { site, siteResponse } from '@/interface/site.interface'
import { SiteService } from '@/services/Site/SiteServive'
import { showToastNotification } from '@/utility/helper'
import { RoutesLink } from '@/utility/RouteLinks'
import { errorTypes } from '@/utility/validation'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { ThrowError } from '@/api/callApi'
import SearchSelect from '@/components/common/SearchSelect'
import { BranchService } from '@/services/Branch/BranchSevice'
import type { branch } from '@/interface/branch.interface'

const AddSite: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams()
    const [isLoading, setIsLoading] = useState(false);
    const [branches, setBranches] = useState<{ value: string; label: string }[]>([]);
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<site>();

    const getBranches = async () => {
        try {
            const response = await BranchService.getBranch();
            if (response?.status) {
                const formatted = (response.data.branch || []).map((b: branch) => ({
                    value: b?.id || "",
                    label: b?.name || ""
                }));
                setBranches(formatted);
            }
        } catch (error) {
            ThrowError(error);
        }
    }

    const getSiteById = async () => {
        if (!id) return;
        try {
            const response = await SiteService.getSiteById(id);
            if (response?.status) {
                const data = response.data;
                setValue("name", data.name);
                setValue("type", data.type);
                setValue("searchedLocation", data.searchedLocation);
                setValue("address", data.address);
                setValue("range", data.range);
                setValue("status", data.status);
                setValue("isPunchAllowed", data.isPunchAllowed);
                setValue("branchId", data.branchId);
                setValue("location", data.location);
            }
        } catch (error) {
            ThrowError(error);
        }
    }

    useEffect(() => {
        getBranches();
        if (id) {
            getSiteById();
        }
    }, [id]);

    const onSubmit = async (data: site) => {
        try {
            setIsLoading(true);
            const payload: site = {
                name: data.name,
                type: data.type,
                searchedLocation: data.searchedLocation,
                address: data.address,
                location: data.location,
                range: data.range,
                status: String(data.status) === "true",
                isPunchAllowed: String(data.isPunchAllowed) === "true",
                branchId: data.branchId,
            }
            let response: siteResponse;
            if (id) {
                response = await SiteService.updateSite(payload, id);
            } else {
                response = await SiteService.createSite(payload);
            }

            if (response?.status) {
                showToastNotification("success", response?.message)
                navigate(RoutesLink.superAdminSite)
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        register("branchId", { ...errorTypes.required("Branch") });
    }, [register]);

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading
                    title={id ? "Edit Site" : "Add Site"}
                    isBackButtonVisible
                    onClick={() => navigate(-1)}
                />
                <Card className='overflow-visible'>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                {...register("name", { ...errorTypes.required('Name') })}
                                type="text"
                                label="Site Name"
                                placeholder="Enter Site Name"
                                error={errors.name?.message}
                            />
                            <FormField
                                {...register("type", { ...errorTypes.required('Type') })}
                                type="text"
                                label="Type"
                                placeholder="e.g. office, campus"
                                error={errors.type?.message}
                            />
                            <FormField
                                {...register("searchedLocation", { ...errorTypes.required('Location') })}
                                type="text"
                                label="Location"
                                placeholder="e.g. Indore, MP"
                                error={errors.searchedLocation?.message}
                            />
                            <FormField
                                {...register("address", { ...errorTypes.required('Address') })}
                                type="text"
                                label="Address"
                                placeholder="Enter full address"
                                error={errors.address?.message}
                            />
                            <FormField
                                {...register("location.lat", { ...errorTypes.required('Latitude'), valueAsNumber: true })}
                                type="number"
                                label="Latitude"
                                placeholder="e.g. 22.7196"
                                error={errors.location?.lat?.message}
                            />
                            <SearchSelect
                                label="Branch"
                                required
                                placeholder="Select Branch"
                                options={branches}
                                value={watch("branchId")}
                                onChange={(val) => setValue("branchId", val)}
                                error={errors.branchId?.message}
                            />
                            <FormField
                                {...register("location.lng", { ...errorTypes.required('Longitude'), valueAsNumber: true })}
                                type="number"
                                label="Longitude"
                                placeholder="e.g. 75.8577"
                                error={errors.location?.lng?.message}
                            />
                            <FormField
                                {...register("range", { ...errorTypes.required('Range') })}
                                type="text"
                                label="Range (meters)"
                                placeholder="e.g. 100"
                                error={errors.range?.message}
                            />
                            <FormField
                                {...register("status", { ...errorTypes.required('Status') })}
                                type="select"
                                value={String(watch("status") ?? "")}
                                options={[
                                    { value: "select", label: "Select Status" },
                                    { value: "true", label: "Active" },
                                    { value: "false", label: "Inactive" },
                                ]}
                                label="Status"
                                error={errors.status?.message}
                            />
                            <FormField
                                {...register("isPunchAllowed", { ...errorTypes.required('Punch Allowed') })}
                                type="select"
                                value={String(watch("isPunchAllowed") ?? "")}
                                options={[
                                    { value: "select", label: "Select Option" },
                                    { value: "true", label: "Yes" },
                                    { value: "false", label: "No" },
                                ]}
                                label="Is Punch Allowed"
                                error={errors.isPunchAllowed?.message}
                            />

                        </div>
                        <div className="flex justify-end">
                            <Button disabled={isLoading} type="submit">
                                {isLoading ? "Submitting..." : "Submit"}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    )
}

export default AddSite;
